import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientAuth } from '../../contexts/ClientAuthContext';

const styles = {
  root: {
    minHeight: '100vh',
    background: '#F4F6F9',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
  },
  header: {
    background: '#0D2C54',
    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  headerInner: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '0 24px',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logoArea: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    textDecoration: 'none',
  },
  logo: {
    height: '34px',
    objectFit: 'contain',
    filter: 'brightness(0) invert(1)',
    opacity: 0.95,
  },
  logoFallback: {
    fontSize: '18px',
    fontWeight: '800',
    color: '#fff',
    letterSpacing: '2px',
  },
  logoSeparator: {
    width: '1px',
    height: '24px',
    background: 'rgba(255,255,255,0.2)',
  },
  portalLabel: {
    fontSize: '11px',
    fontWeight: '500',
    color: 'rgba(255,255,255,0.55)',
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  rightArea: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  clientName: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: '13px',
    fontWeight: '500',
    textAlign: 'right',
    maxWidth: '240px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  logoutBtn: {
    padding: '7px 16px',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '6px',
    color: 'rgba(255,255,255,0.85)',
    fontSize: '12px',
    fontWeight: '500',
    cursor: 'pointer',
    letterSpacing: '0.3px',
    transition: 'background 0.2s',
    fontFamily: 'inherit',
    flexShrink: 0,
  },
  main: {
    flex: 1,
    maxWidth: '1100px',
    width: '100%',
    margin: '0 auto',
    padding: '32px 24px 48px',
    boxSizing: 'border-box',
  },
  footer: {
    background: '#fff',
    borderTop: '1px solid #E4E8EF',
    padding: '20px 24px',
    textAlign: 'center',
    color: '#8a9ab0',
    fontSize: '12px',
    letterSpacing: '0.3px',
  },
};

export default function ClientLayout({ children }) {
  const navigate = useNavigate();
  const { clientUser, clientLogout } = useClientAuth();

  React.useEffect(() => { document.title = 'ACS Client Portal'; }, []);

  const handleLogout = () => {
    clientLogout();
    navigate('/portal/login');
  };

  return (
    <div style={styles.root}>
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div style={styles.logoArea}>
            <img
              src="/img/acs-nav-logo.png"
              alt="ACS"
              style={styles.logo}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'inline';
              }}
            />
            <span style={{ ...styles.logoFallback, display: 'none' }}>ACS</span>
            <div style={styles.logoSeparator} />
            <span style={styles.portalLabel}>Client Portal</span>
          </div>

          <div style={styles.rightArea}>
            {clientUser && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={styles.clientName}>
                  {clientUser.contact_name || clientUser.client_name}
                </div>
                {clientUser.employee && (
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    background: '#D4A853',
                    color: '#0D2C54',
                    borderRadius: 4,
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    flexShrink: 0,
                  }}>
                    ACS Staff
                  </span>
                )}
              </div>
            )}
            <button
              onClick={handleLogout}
              style={styles.logoutBtn}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.18)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main style={styles.main}>
        {children}
      </main>

      <footer style={styles.footer}>
        Audio Command Systems, Inc. &nbsp;|&nbsp; Making Technology Simple Since 1976
        &nbsp;&nbsp;&bull;&nbsp;&nbsp;
        (888) 724-3004 &nbsp;&nbsp;&bull;&nbsp;&nbsp;
        <a
          href="https://www.audiocommand.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1A5490', textDecoration: 'none' }}
        >
          www.audiocommand.com
        </a>
      </footer>
    </div>
  );
}
